import React, { useEffect, useMemo, useState } from 'react';
import { ReceivingTable, ReceiveGoods } from './components';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import { ReceivingOrder, ReceiveGoodsData } from './receivingTypes';
import { usePurchaseOrdersStore } from '../../../store/purchaseOrdersStore';
import { PurchaseOrder, ReceivingVariance, listReceivingVariances } from '../../../services/purchaseOrdersApi';
import styles from './WarehouseReceiving.module.css';

function adapt(po: PurchaseOrder): ReceivingOrder {
  return {
    id: String(po.id),
    vendorName: po.supplier_name || '—',
    poReference: po.po_number,
    expectedDate: po.expected_date
      ? new Date(po.expected_date).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }).toUpperCase()
      : '—',
    status: po.status === 'received' ? 'Completed' : po.status === 'partial' ? 'Partial' : 'Pending',
  };
}

function buildGoods(po: PurchaseOrder | null): ReceiveGoodsData {
  if (!po) return { poReference: '', vendorName: '', items: [] };
  return {
    poReference: po.po_number,
    vendorName: po.supplier_name || '—',
    items: (po.items ?? []).map((it) => ({
      id: String(it.id),
      skuId: it.sku_id,
      productName: it.product_name,
      sku: it.sku_code,
      expectedQty: `${it.quantity_ordered}`,
      alreadyReceived: String(it.quantity_received),
      receivedQty: '', // qty received THIS event
      batchLot: '',
      expiryDate: '',
      location: '',
      damaged: false,
      damagedQty: '',
      notes: '',
    })),
  };
}

const WarehouseReceiving: React.FC = () => {
  const items = usePurchaseOrdersStore((s) => s.items);
  const current = usePurchaseOrdersStore((s) => s.current);
  const loading = usePurchaseOrdersStore((s) => s.loading);
  const error = usePurchaseOrdersStore((s) => s.error);
  const fetchList = usePurchaseOrdersStore((s) => s.fetchList);
  const fetchOne = usePurchaseOrdersStore((s) => s.fetchOne);
  const receive = usePurchaseOrdersStore((s) => s.receive);
  const saving = usePurchaseOrdersStore((s) => s.saving);

  const [goodsData, setGoodsData] = useState<ReceiveGoodsData>(buildGoods(null));
  const [receiveError, setReceiveError] = useState<string | null>(null);
  const [variances, setVariances] = useState<ReceivingVariance[]>([]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    setGoodsData(buildGoods(current));
    setVariances([]);
  }, [current]);

  const orders = useMemo<ReceivingOrder[]>(() => items.map(adapt), [items]);

  const handleSelectOrder = async (order: ReceivingOrder) => {
    await fetchOne(order.id);
  };

  const handleItemChange = (id: string, field: string, value: string | boolean) => {
    setGoodsData((prev) => ({
      ...prev,
      items: prev.items.map((item) =>
        item.id === id ? { ...item, [field]: value } : item
      ),
    }));
  };

  // Build the goods-receipt from lines the user actually received (qty > 0).
  // The backend decides whether the resulting PO status is 'partial' or 'received'.
  const submitReceipt = async () => {
    if (!current) return;
    setReceiveError(null);
    setVariances([]);

    const receivedItems = goodsData.items.filter((it) => Number(it.receivedQty) > 0);

    // Damaged qty can't exceed what was actually received this event —
    // mirrors the backend's own validation, checked client-side first for a
    // faster error.
    const overDamaged = receivedItems.find(
      (it) => it.damaged && Number(it.damagedQty) > Number(it.receivedQty)
    );
    if (overDamaged) {
      setReceiveError(`Damaged qty for "${overDamaged.productName}" can't exceed the received qty.`);
      return;
    }

    const lines = receivedItems.map((it) => ({
      po_item_id: Number(it.id),
      quantity: Number(it.receivedQty),
      lot_number: it.batchLot.trim(),
      expiration_date: it.expiryDate,
      location_code: it.location.trim(),
      damaged_quantity: it.damaged ? Number(it.damagedQty) || 0 : 0,
    }));

    if (lines.length === 0) {
      setReceiveError('Enter a received quantity for at least one item.');
      return;
    }
    const invalid = lines.find((l) => !l.lot_number || !l.expiration_date || !l.location_code);
    if (invalid) {
      setReceiveError('Each received line needs a batch/lot, expiry date, and location.');
      return;
    }

    try {
      await receive(current.id, lines);
      await fetchList();
      await fetchOne(current.id); // refresh the receive panel with new running totals
      // Surface any variance recorded by this receipt (short/over count or
      // damage) — receivePurchaseOrder's response is just the updated PO, so
      // pull it from the review queue instead.
      try {
        const all = await listReceivingVariances(true);
        const poId = Number(current.id);
        const justNow = Date.now() - 15000;
        setVariances(
          all.filter((v) => v.purchase_order_id === poId && new Date(v.created_at).getTime() >= justNow)
        );
      } catch {
        // Non-critical — the receipt itself already succeeded.
      }
    } catch (err) {
      setReceiveError(err instanceof Error ? err.message : 'Failed to receive goods');
    }
  };

  return (
    <div className={styles.container}>
      <AsyncBoundary
        loading={loading && items.length === 0}
        error={error}
        empty={!loading && orders.length === 0}
        emptyMessage="No incoming purchase orders"
        onRetry={() => fetchList()}
      >
        <ReceivingTable orders={orders} onSelectOrder={handleSelectOrder} />
      </AsyncBoundary>
      {current && (
        <ReceiveGoods
          data={goodsData}
          onItemChange={handleItemChange}
          onConfirm={submitReceipt}
          submitting={saving}
          error={receiveError}
          variances={variances}
        />
      )}
    </div>
  );
};

export const WarehouseReceivingPage: React.FC = () => <WarehouseReceiving />;
export default WarehouseReceiving;
